import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Calendar, Users, ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react';
import { eventsService } from '../../services/eventsService.js';
import styles from './EventsList.module.css';

function statusLabel(status) {
  return { draft: 'Draft', published: 'Live', cancelled: 'Cancelled' }[status] || status;
}

function statusClass(status) {
  return styles[`status_${status}`] || '';
}

function CapacityMeter({ registered, capacity }) {
  const safeCapacity = Math.max(1, capacity);
  const pct = Math.min(100, Math.round((registered / safeCapacity) * 100));
  const full = registered >= capacity;
  let tone = 'ok';
  if (full) tone = 'full';
  else if (pct >= 95) tone = 'red';
  else if (pct >= 80) tone = 'amber';
  return (
    <span className={styles.capacityWrap}>
      <span className={styles.capacityLabel}>
        <Users size={13} />
        {full ? 'Sold out' : `${registered} / ${capacity} registered`}
      </span>
      <span className={styles.capacityBar} aria-hidden="true">
        <span
          className={`${styles.capacityFill} ${styles[`capacityFill_${tone}`]}`}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
}

export default function EventsList() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await eventsService.listEvents();
        if (active) setEvents(list);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Link to="/dashboard" className={styles.back}>
          <ArrowLeft size={14} />
          Dashboard
        </Link>

        <div className={styles.header}>
          <h1 className={styles.title}>Events</h1>
          <button className={styles.newBtn} onClick={() => navigate('/events/new')}>
            <Plus size={14} />
            New event
          </button>
        </div>

        {loading && (
          <div className={styles.loadingWrap}>
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>Loading events…</p>
          </div>
        )}

        {error && !loading && (
          <div className={styles.errorBox} role="alert">
            <AlertCircle size={16} className={styles.errorIcon} />
            <p className={styles.errorText}>{error}</p>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className={styles.emptyState}>
            <Calendar size={40} className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>No events yet</p>
            <p className={styles.emptyBody}>Create your first event to get started.</p>
            <button className={styles.newBtn} onClick={() => navigate('/events/new')}>
              <Plus size={14} /> New event
            </button>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className={styles.list}>
            {events.map((ev) => (
              <article key={ev.id} className={styles.card}>
                <div className={styles.cardMain}>
                  <div className={styles.cardTop}>
                    <span className={`${styles.status} ${statusClass(ev.status)}`}>
                      {statusLabel(ev.status)}
                    </span>
                    <h2 className={styles.cardTitle}>{ev.title}</h2>
                  </div>
                  <div className={styles.cardMeta}>
                    <span className={styles.metaItem}>
                      <Calendar size={13} />
                      {new Date(ev.starts_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    <CapacityMeter registered={ev.registered_count ?? 0} capacity={ev.capacity} />
                  </div>
                </div>
                <div className={styles.cardActions}>
                  {ev.status === 'published' && (
                    <a
                      href={`/e/${ev.short_code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.actionLink}
                    >
                      <ExternalLink size={13} /> Public
                    </a>
                  )}
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => navigate(`/events/${ev.id}/attendees`)}
                  >
                    Attendees
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtnPrimary}
                    onClick={() => navigate(`/events/${ev.id}/edit`)}
                  >
                    Edit
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
